import { useCallback } from 'react';
import { addRecentWorkspaceItem } from '../../utils/recentWorkspaceItems';
import {
  mergeViewIntoSprintOptions,
  isTemplateItem,
  buildCampaignTabUpdates,
  clearedMissionOverlayFields,
} from './utils';

/**
 * In-tab selection: update the active tab and navigate to sprint/template/view content.
 */
export function useTabSelection({
  activeTab,
  updateTab,
  openTab,
  handleSelectSprint,
  handleSelectTemplate,
  handleSelectView,
  preferences,
}) {
  // Defined before handleSelectSprintAndUpdateTab (breadcrumb back to template root uses onSelectSprint)
  const handleSelectTemplateAndUpdateTab = useCallback(async (template, options = {}) => {
    const merged = mergeViewIntoSprintOptions(options, activeTab, preferences, template.id);
    // Template views are per-template — never carry a nested campaign's customViewId
    const viewMode = typeof merged.view === 'object' && merged.view?.type
      ? merged.view.type
      : (typeof merged.view === 'string' ? merged.view : activeTab?.viewMode || preferences.defaultView || 'flow');

    const sameTemplate = activeTab?.type === 'template' && activeTab.targetId === template.id;
    const tabFields = {
      type: 'template',
      targetId: template.id,
      label: template.name,
      icon: template.icon || '📋',
      viewMode,
      customViewId: sameTemplate ? (activeTab.customViewId ?? null) : null,
    };
    if (activeTab) {
      const isNewContent = activeTab.targetId !== template.id || activeTab.type !== 'template';
      updateTab(activeTab.id, {
        ...tabFields,
        ...(isNewContent ? clearedMissionOverlayFields() : {}),
      }, { addToHistory: isNewContent });
    } else {
      openTab(tabFields);
    }

    addRecentWorkspaceItem({
      type: 'template',
      id: template.id,
      label: template.name,
      icon: template.icon,
    });

    await handleSelectTemplate(template, { ...merged, view: viewMode });
    return { ...merged, view: viewMode };
  }, [activeTab, updateTab, openTab, handleSelectTemplate, preferences]);

  const handleSelectSprintAndUpdateTab = useCallback(async (sprint, options = {}) => {
    if (isTemplateItem(sprint)) {
      return handleSelectTemplateAndUpdateTab(sprint, options);
    }

    const merged = mergeViewIntoSprintOptions(options, activeTab, preferences, sprint.id);
    const v = merged.view;

    if (activeTab) {
      const { tabUpdates, isNewContent } = buildCampaignTabUpdates(
        activeTab,
        sprint,
        v,
        preferences,
      );
      updateTab(activeTab.id, tabUpdates, { addToHistory: isNewContent });
    } else {
      openTab({
        type: 'campaign',
        targetId: sprint.id,
        label: sprint.name,
        icon: sprint.icon,
        viewMode: typeof v === 'object' && v?.type ? v.type : (typeof v === 'string' ? v : preferences.defaultView || 'flow'),
        customViewId: typeof v === 'object' ? v.customViewId || null : null,
      });
    }

    addRecentWorkspaceItem({
      type: 'campaign',
      id: sprint.id,
      label: sprint.name,
      icon: sprint.icon,
    });

    await handleSelectSprint(sprint, merged);
    return merged;
  }, [activeTab, updateTab, openTab, handleSelectSprint, handleSelectTemplateAndUpdateTab, preferences]);

  const handleSelectViewAndUpdateTab = useCallback(async (view) => {
    const tabFields = {
      type: 'view',
      targetId: view.id,
      label: view.name,
      icon: view.icon || '📊',
      viewMode: activeTab?.viewMode || preferences.defaultView || 'flow',
    };
    if (activeTab) {
      const isNewContent = activeTab.targetId !== view.id || activeTab.type !== 'view';
      updateTab(activeTab.id, {
        ...tabFields,
        ...(isNewContent ? clearedMissionOverlayFields() : {}),
      }, { addToHistory: isNewContent });
    } else {
      openTab(tabFields);
    }

    addRecentWorkspaceItem({
      type: 'view',
      id: view.id,
      label: view.name,
      icon: view.icon || '📊',
    });

    await handleSelectView(view);
  }, [activeTab, updateTab, openTab, handleSelectView, preferences.defaultView]);

  return {
    handleSelectSprintAndUpdateTab,
    handleSelectTemplateAndUpdateTab,
    handleSelectViewAndUpdateTab,
  };
}
